import React, { useEffect, useRef } from 'react';
import ProgressBarComponents from './ProgressBarComponents.jsx';
import { useTranslation } from 'react-i18next';

const IngredientParametersModal = ({ ingredient, isOpen, onClose }) => {
  const panelRef = useRef(null);
  const { t } = useTranslation();
  useEffect(() => {
    if (!isOpen) return undefined;
    const id = window.setTimeout(() => {
      panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
    return () => window.clearTimeout(id);
  }, [isOpen]);

  if (!isOpen || !ingredient) return null;

  const groupDefs = [
    {
      key: 'environment',
      title: 'Miljö',
      icon: '/assets/icons/svg/Miljo.svg',
      items: [
        { key: 'climate_score', label: 'Klimat' },
        { key: 'water_score', label: 'Vatten' },
        { key: 'biodiversity_score', label: 'Biologisk mångfald' },
        { key: 'transport_score', label: 'Transport' },
        { key: 'land_use_score', label: 'Landanvändning' },
        { key: 'fertilizers_use_score', label: 'Övergödning' },
        { key: 'pesticides_use_score', label: 'Bekämpningsmedel' },
      ],
    },
    {
      key: 'health',
      title: 'Hälsa',
      icon: '/assets/icons/svg/Halsa.svg',
      items: [
        { key: 'health_score', label: 'Hälsa' },
        { key: 'antibiotics_resistance_score', label: 'Antibiotikaresistens' },
      ]
    },
    {
      key: 'social',
      title: 'Rättvisa',
      icon: '/assets/icons/svg/Rattvisa.svg',
      items: [
        { key: 'social_justice_score', label: 'Social rättvisa' },
        { key: 'child_labor_score', label: 'Barnarbete' },
        { key: 'animal_protection_score', label: 'Djurvälfärd' },
        { key: 'worker_rights_score', label: 'Arbetsförhållanden' },
        { key: 'equality_score', label: 'Jämlikhet' },
        { key: 'local_population_score', label: 'Lokalbefolkning' }
      ],
    },
  ];

  const score = (val) => (typeof val === 'number' && !Number.isNaN(val) ? val : null);

  const renderGroup = (group) => {
    const values = group.items
      .map((it) => ({ ...it, value: score(ingredient[it.key]) }))
      .filter((it) => it.value != null);

    if (values.length === 0) return null;

    // Compute average; for health, base only on 'health_score' (exclude antibiotics_resistance_score)
    const valuesForAverage =
      group.key === 'health'
        ? values.filter((it) => it.key === 'health_score')
        : values.filter((it) => it.value != null && it.value > 0); // ignore 0 values

    const average =
      valuesForAverage.length > 0
        ? Math.round(
          valuesForAverage.reduce((sum, it) => sum + it.value, 0) /
          valuesForAverage.length
        )
        : 0;

    return (
      <div key={group.key} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center space-x-3">
            <img src={group.icon} alt={group.title} className="w-8 h-8" />
            <h4 className="text-xl font-bold text-gray-900">{ t(group.title)}</h4>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-gray-900">{average}</div>
            <div className="text-sm text-gray-500">{t("Genomsnitt")}</div>
          </div>
        </div>
        
        <div className="space-y-3">
          {values.map((param, index) => {
            return (
              <ProgressBarComponents
                key={index}
                items={[{ key: param.key, value: param.value, label: param.label }]}
                iconSize={32}
              />
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center max-h-screen">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-modern opacity-100 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal content */}
      <div ref={panelRef} className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-w-6xl mx-4 max-h-[90vh] overflow-hidden animate-[fadeIn_0.2s_ease-out]">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-secondary-green/20 to-secondary-green/40 rounded-full flex items-center justify-center">
              <img src="/assets/icons/svg/parameter.svg" alt="Parameters" className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">{t("Ingrediensparametrar")}</h3>
              <p className="text-sm text-gray-600">{ingredient.name}</p>
            </div>
          </div>
          <button
            className="inline-flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 focus:outline-none transition-colors"
            onClick={onClose}
            aria-label="Stäng"
          >
            <img src="/assets/icons/svg/Stang.svg" alt="Stäng" className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Ingredient Info */}
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <div className="grid grid-cols-2 tablet:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-500">{t("Mängd")}:</span>
                <p className="font-semibold">{ingredient.amount}g ({ingredient.percentage}%)</p>
              </div>
              <div>
                <span className="text-gray-500">{t("Ursprung/Produktion")}:</span>
                <p className="font-semibold">{ingredient.origin || 'Okänt'}</p>
              </div>
              <div>
                <span className="text-gray-500">CO2e:</span>
                <p className="font-semibold">
                  {(() => {
                    const co2Value = parseFloat(ingredient.co2e_equivalent);
                    // const percentage = parseFloat(ingredient.percentage);
                    if (Number.isFinite(co2Value)) {
                      return `${(co2Value).toFixed(2)} kg/kg`;
                    }
                    return '0 kg/kg';
                  })()}
                </p>
              </div>
              <div>
                <span className="text-gray-500">{t("Typ")}:</span>
                <p className="font-semibold capitalize">
                  {ingredient.is_eco ? t("Ekologisk") : t("Konventionell")}
                </p>
              </div>
            </div>
          </div>

          {/* Parameters Groups */}
          <div className="space-y-6">
            <div className="grid grid-cols-1 tablet:grid-cols-3 laptop:grid-cols-3 desktop:grid-cols-3 gap-6">
              {groupDefs.map((g) => renderGroup(g))}
            </div>
          </div>

          {/* Additional Info */}
          {ingredient.comment && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <h4 className="font-semibold text-blue-900 mb-2">Kommentar</h4>
              <p className="text-sm text-blue-800">{ingredient.comment}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IngredientParametersModal;

